//////////////////
// IMPORTS
//////////////////
// schema
import { EcosystemEnum } from "@clockwise/schema";
// libraries
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import React from "react";
import toast from "react-hot-toast";

import { UpdateNotificationSettingMutationDocument } from "#webapp/src/components/web-settings/web-settings-emails-and-notifications/__generated__/UpdateNotificationSetting.generated";
import {
  browserNotificationPermissionGranted,
  receiveMeetingFlexibilitySuggestionNotification,
} from "#webapp/src/components/web-settings/web-settings-emails-and-notifications/web-settings-emails-and-notifications";
import { BrowserNotificationPermissionBannerQueryDocument } from "./__generated__/BrowserNotificationPermissionBanner.generated";

// components
import { BrowserNotificationPermissionDialog } from "#webapp/src/components/web-settings/web-settings-emails-and-notifications";
import {
  NotificationProvider,
  useNotification,
} from "#webapp/src/components/web-settings/web-settings-emails-and-notifications/useNotification";

// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { requestNotificationPermission } from "#webapp/src/util/notification.util";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import { ServiceWorkerPostMessagePayload } from "#webapp/src/util/service-worker.util";

// state
import { browserNotificationBannerDismissTimestamps } from "@clockwise/web-commons/src/util/local-storage";

// material-ui
import { Close } from "@clockwise/design-system/icons";
import { Notifications, Warning } from "@clockwise/icons";
import { IconButton } from "@material-ui/core";

// ~-~-~-~-~-~-~-
// Browser Notification Permission Banner
// ~-~-~-~-~-~-~-
enum LocalStorageKey {
  FirstDismiss = "FirstDismiss",
  SecondDismiss = "SecondDismiss",
}

const BrowserNotificationPermissionBanner = () => {
  const {
    isOnBrowserNotifications,
    ecosystem,
    showBrowserNotificationPermissionDialog,
    setShowBrowserNotificationPermissionDialog,
  } = useNotification();

  const [status, setStatus] = React.useState<"default" | "error" | "reminder">("default");
  const [showBanner, setShowBanner] = React.useState(false);

  const [updateSetting] = useMutation(UpdateNotificationSettingMutationDocument, {
    onError: (error) => {
      logger.error(`failed to updateSetting: ${error.message}`, error);
    },
  });

  React.useEffect(() => {
    const firstDismiss = browserNotificationBannerDismissTimestamps.get(
      LocalStorageKey.FirstDismiss,
    );
    const secondDismiss = browserNotificationBannerDismissTimestamps.get(
      LocalStorageKey.SecondDismiss,
    );

    if (secondDismiss) {
      return;
    }

    if (!firstDismiss || Math.abs(DateTime.fromISO(firstDismiss).diffNow("days").days) > 2) {
      // if last dismissed over 2 days ago or never dismissed, show banner
      track(TrackingEvents.BANNERS.BROWSER_NOTIFICATION_PERMISSION_BANNER_SHOWN);
      setShowBanner(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { loading, data, error } = useQuery(BrowserNotificationPermissionBannerQueryDocument);

  if (!isOnBrowserNotifications || ecosystem !== EcosystemEnum.Microsoft || !showBanner) {
    return <></>;
  }

  if (error) {
    logger.error(`failed to load BrowserNotificationPermissionBanner: ${error.message}`, error);
    return <></>;
  }

  if (!data || loading) {
    return <></>;
  }

  const viewer = data.viewer;
  const org = getCurrentOrg(viewer);

  if (!org) {
    return <></>;
  }

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const requestBrowserNotificationPermission = async () => {
    // if permission is already denied in browser, show dialog
    if (Notification.permission === "denied") {
      return setShowBrowserNotificationPermissionDialog(true);
    }

    // request notification
    const permission = await requestNotificationPermission();
    const isNotificationPermissionGranted = permission === "granted";

    // save response
    await updateSetting({
      variables: {
        input: {
          setting: browserNotificationPermissionGranted.get(org)!,
          value: [isNotificationPermissionGranted ? "true" : "false"],
        },
      },
    });

    await updateSetting({
      variables: {
        input: {
          setting: receiveMeetingFlexibilitySuggestionNotification.get(org)!,
          value: [isNotificationPermissionGranted ? "true" : "false"],
        },
      },
    });

    track(TrackingEvents.BANNERS.BROWSER_NOTIFICATION_PERMISSION_BANNER_SET, {
      isOn: isNotificationPermissionGranted,
    });

    // handle not granted
    if (!isNotificationPermissionGranted) {
      toast.error("Failed to grant notification permission in your browser");
      return;
    }

    // handle granted
    await navigator.serviceWorker.ready.then((registration) => {
      const payload: ServiceWorkerPostMessagePayload = {
        message: "cw-notification-permission-granted",
        data: {},
      };
      registration.active?.postMessage(payload);
    });

    browserNotificationBannerDismissTimestamps.set(
      LocalStorageKey.SecondDismiss,
      DateTime.now().toISO(),
    );

    toast.success("Success! Push notifications have been enabled.");
    return;
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  if (browserNotificationPermissionGranted.isOn(org) && Notification.permission === "granted") {
    return <></>;
  }

  const renderDefault = () => {
    return (
      <>
        <Notifications className="cw-mr-2" />
        Get notifications on your desktop when Clockwise is able to maximize your Focus Time.{" "}
        <a
          className="cw-ml-2 cw-underline cw-cursor-pointer"
          onClick={() => void requestBrowserNotificationPermission()}
        >
          Turn on
        </a>
      </>
    );
  };

  const renderError = () => {
    return (
      <>
        <Notifications className="cw-mr-2" />
        Couldn't enable notifications.{" "}
        <a
          className="cw-ml-2 cw-underline cw-cursor-pointer"
          onClick={() => {
            setStatus("reminder");
          }}
        >
          Try again
        </a>
      </>
    );
  };

  const renderReminder = () => {
    return (
      <>
        <Warning className="cw-mr-2" />
        We strongly recommend enabling push notifications to get the most value from Clockwise.{" "}
        <a
          className="cw-ml-2 cw-underline cw-cursor-pointer"
          onClick={() => void requestBrowserNotificationPermission()}
        >
          Turn on
        </a>
      </>
    );
  };

  return (
    <>
      <div className="cw-w-full cw-flex cw-items-center cw-justify-center cw-bg-info-emphasis cw-text-white cw-relative cw-h-12">
        <div className="cw-flex cw-items-center">
          {status === "default" && renderDefault()}
          {status === "error" && renderError()}
          {status === "reminder" && renderReminder()}
        </div>
        <IconButton className="cw-absolute cw-right-2">
          <Close
            className="cw-text-white"
            onClick={() => {
              const firstDismiss = browserNotificationBannerDismissTimestamps.get(
                LocalStorageKey.FirstDismiss,
              );

              const key = firstDismiss
                ? LocalStorageKey.SecondDismiss
                : LocalStorageKey.FirstDismiss;

              browserNotificationBannerDismissTimestamps.set(key, DateTime.now().toISO());
              setShowBanner(false);
            }}
          />
        </IconButton>
      </div>
      {showBrowserNotificationPermissionDialog && (
        <BrowserNotificationPermissionDialog
          onClose={() => setShowBrowserNotificationPermissionDialog(false)}
        />
      )}
    </>
  );
};

export const BrowserNotificationPermissionBannerWrapper = () => {
  return (
    <NotificationProvider>
      <BrowserNotificationPermissionBanner />
    </NotificationProvider>
  );
};
